import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { getMenuCategories } from '../../lib/sanity';
import { useNav } from '../../context/NavContext';

// Helper function to extract plain text from portable text
const extractPlainText = (portableText) => {
  if (!portableText) return '';
  if (typeof portableText === 'string') return portableText;
  if (Array.isArray(portableText)) {
    return portableText
      .map(block => {
        if (block._type === 'block' && block.children) {
          return block.children.map(child => child.text).join('');
        }
        return '';
      })
      .join(' ');
  }
  return '';
};

/**
 * MegaMenu component for displaying menu categories in a dropdown
 * Styled to match the LocationsDropdown component
 * 
 * @returns {JSX.Element} MegaMenu component
 */
export default function MegaMenu() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const menuRef = useRef(null);
  const { isDropdownActive, toggleDropdown, closeDropdown } = useNav();
  
  const isOpen = isDropdownActive('menu');
  
  // Fetch menu categories when component mounts
  useEffect(() => {
    async function fetchCategories() {
      try {
        setLoading(true);
        const data = await getMenuCategories();
        setCategories(data || []);
      } catch (error) {
        console.error('Error fetching menu categories:', error);
      } finally {
        setLoading(false);
      }
    }
    
    fetchCategories();
  }, []);
  
  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        closeDropdown('menu');
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [closeDropdown]);

  // Group categories into two columns for better layout
  const organizeCategories = () => {
    if (!categories || categories.length === 0) return { leftColumn: [], rightColumn: [] };
    
    const sortedCategories = [...categories].sort((a, b) => {
      if (a.order !== undefined && b.order !== undefined) {
        return a.order - b.order;
      }
      if (a.order !== undefined) return -1;
      if (b.order !== undefined) return 1;
      return a.name.localeCompare(b.name);
    });
    
    const midpoint = Math.ceil(sortedCategories.length / 2);
    return {
      leftColumn: sortedCategories.slice(0, midpoint),
      rightColumn: sortedCategories.slice(midpoint)
    };
  };
  
  const { leftColumn, rightColumn } = organizeCategories();
  
  return (
    <div className="relative" ref={menuRef}>
      {/* Menu Trigger */}
      <button 
        className="text-brand-navy hover:text-brand-orange transition-colors flex items-center"
        onClick={() => toggleDropdown('menu')}
        aria-expanded={isOpen}
        aria-haspopup="true"
        onMouseEnter={() => toggleDropdown('menu')}
      >
        <span>MENU</span>
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          className={`h-4 w-4 ml-1 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
          fill="none" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      {/* Mega Menu Dropdown */}
      {isOpen && (
        <div 
          className="absolute left-0 mt-6 bg-brand-cream border border-brand-navy rounded-lg shadow-xl z-50 w-[700px] max-w-[95vw]"
          onMouseLeave={() => closeDropdown('menu')}
        >
          {/* Top banner */}
          <div className="bg-brand-navy text-white py-4 px-6 rounded-t-lg flex justify-between items-center">
            <h3 className="text-xl font-bold">OUR MENU</h3>
            <Link 
              to="/menu" 
              className="text-white hover:text-brand-yellow flex items-center text-sm bg-brand-navy bg-opacity-50 hover:bg-opacity-70 px-4 py-2 rounded-full transition-all"
              onClick={() => closeDropdown('menu')}
            >
              View Full Menu
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </Link>
          </div>
          
          <div className="p-6">
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-navy"></div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Left Column */}
                <div>
                  {leftColumn.map(category => (
                    <div key={category._id} className="mb-6 last:mb-0">
                      <div className="flex mb-3">
                        <div className="w-12 h-12 bg-brand-navy rounded-full flex-shrink-0 flex items-center justify-center mr-3 overflow-hidden">
                          {category.imageUrl ? (
                            <img 
                              src={category.imageUrl} 
                              alt={category.name} 
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                            </svg>
                          )}
                        </div>
                        <div>
                          <Link
                            to={`/menu/${category.slug}`}
                            className="text-lg font-bold text-brand-navy hover:text-brand-orange transition-colors block"
                            onClick={() => closeDropdown('menu')}
                          >
                            {category.name}
                          </Link>
                          {category.description && (
                            <p className="text-sm text-gray-700 line-clamp-2 mt-1">{extractPlainText(category.description)}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Right Column */}
                <div>
                  {rightColumn.map(category => (
                    <div key={category._id} className="mb-6 last:mb-0">
                      <div className="flex mb-3">
                        <div className="w-12 h-12 bg-brand-navy rounded-full flex-shrink-0 flex items-center justify-center mr-3 overflow-hidden">
                          {category.imageUrl ? (
                            <img 
                              src={category.imageUrl} 
                              alt={category.name} 
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                            </svg>
                          )}
                        </div>
                        <div>
                          <Link
                            to={`/menu/${category.slug}`}
                            className="text-lg font-bold text-brand-navy hover:text-brand-orange transition-colors block"
                            onClick={() => closeDropdown('menu')}
                          >
                            {category.name}
                          </Link>
                          {category.description && (
                            <p className="text-sm text-gray-700 line-clamp-2 mt-1">{extractPlainText(category.description)}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div className="bg-brand-navy bg-opacity-10 p-4 flex justify-between items-center">
            <div className="text-sm text-gray-600">
              <p>Prices and menu items may vary by location</p>
            </div>
            <Link 
              to="/locations" 
              className="text-brand-navy hover:text-brand-orange text-sm flex items-center"
              onClick={() => closeDropdown('menu')}
            >
              Find a Location
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}